import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

import { axiosPost } from "../../api/Axios";

export const submitPainEntry = createAsyncThunk(
  "painTracking/createPainTrackingEntry",
  async (painEntry: IPainEntryFormSliceProp, { rejectWithValue }) => {
    try {
      const response = await axiosPost<
        IPainEntryFormSliceProp,
        CreateOrUpdateApisResponse
      >(
        "/painTracking/createPainTrackingEntry", // Replace with your pain entry endpoint
        painEntry
      );
      return response; // Return the success message or any data you want
    } catch (error) {
      return rejectWithValue(error); // Return the error message
    }
  }
);

const initialState: IPainEntryFormSliceProp = {
  painRating: 3,
  errors: [],
  isError: false,
  painTypes: [],
  painDuration: "",
  reliefMethods: [],
  reliefEffectiveness: "",
  showNotes: false,
  notes: "",
  additionalDetailsClicked: false,
  energyLevel: "",
  symptoms: [],
  symptomDuration: "",
  triggerTracking: [],
  moodLevel: "",
  isSubmitting: false,
  painLocations: [],
  painEntrySuccess: null,
};
const resetErrorState = (state: IPainEntryFormSliceProp) => {
  state.isError = false;
  state.errors = [];
};
export const painEntryFromSlice = createSlice({
  name: "painEntryForm",
  initialState,
  reducers: {
    handleResetForm: (state, action: PayloadAction<number>) => {
      state = initialState;
    },
    handlePainLevelChange: (state, action: PayloadAction<number>) => {
      resetErrorState(state);
      const painRating = action.payload;
      state.painRating = painRating;
    },
    handlePainTypeChange: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      const painType = action.payload;
      const currentIndex = state.painTypes.indexOf(painType);

      if (currentIndex === -1) {
        // If painType is not in the array, add it
        state.painTypes.push(painType);
      } else {
        // If painType is in the array, remove it
        state.painTypes.splice(currentIndex, 1);
      }
    },
    handleChangePainDuration: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      state.painDuration = action.payload;
    },
    handleReliefMethodChange: (state, action: PayloadAction<string>) => {
      resetErrorState(state);

      const reliefMethod = action.payload;

      const currentIndex = state.reliefMethods.indexOf(reliefMethod);

      if (currentIndex === -1) {
        state.reliefMethods.push(reliefMethod);
      } else {
        state.reliefMethods.splice(currentIndex, 1);
      }
    },

    handleChangeReliefEffectiveness: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      state.reliefEffectiveness = action.payload;
    },
    handleChangeShowNotes: (state, action: PayloadAction<boolean>) => {
      resetErrorState(state);
      state.showNotes = action.payload;
    },
    handleUpdateNotes: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      state.notes = action.payload;
    },
    handleAddAdditionalDetails: (state, action: PayloadAction<boolean>) => {
      resetErrorState(state);
      state.additionalDetailsClicked = action.payload;
    },
    handleUpdateEnergyLevel: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      state.energyLevel = action.payload;
    },
    handleSymptomChange: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      const symptom = action.payload;

      const currentIndex = state.symptoms.indexOf(symptom);

      if (currentIndex === -1) {
        state.symptoms.push(symptom);
      } else {
        state.symptoms.splice(currentIndex, 1);
      }
    },
    handleUpdateSymptomDuration: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      state.symptomDuration = action.payload;
    },
    handleTriggerChange: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      const trigger = action.payload;

      const currentIndex = state.triggerTracking.indexOf(trigger);

      if (currentIndex === -1) {
        state.triggerTracking.push(trigger);
      } else {
        state.triggerTracking.splice(currentIndex, 1);
      }
    },
    handleUpdateMoodLevel: (state, action: PayloadAction<string>) => {
      resetErrorState(state);
      state.moodLevel = action.payload;
    },
    handlePainEntryFormSubmission(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },
    handleUpdatePainLocations(state, action: PayloadAction<string[]>) {
      resetErrorState(state);
      state.painLocations = action.payload;
    },
    handleUpdateErrorsOfForm(state, action: PayloadAction<IUpdateErrors>) {
      state.isError = action.payload.isError;
      state.errors = action.payload.errors;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitPainEntry.pending, (state) => {
        state.isSubmitting = true; // Set isSubmitting to true while the request is pending
        state.isError = false; // Reset error
      })
      .addCase(submitPainEntry.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.painEntrySuccess = action.payload; // Assume your API returns a message
      })
      .addCase(submitPainEntry.rejected, (state, action) => {
        state.isSubmitting = false;
        state.isError = true; // Handle error message
        state.errors = [action.payload as string];
      });
  },
});

export const {
  handlePainLevelChange,
  handlePainTypeChange,
  handleChangePainDuration,
  handleReliefMethodChange,
  handleChangeReliefEffectiveness,
  handleChangeShowNotes,
  handleUpdateNotes,
  handleAddAdditionalDetails,
  handleUpdateEnergyLevel,
  handleSymptomChange,
  handleUpdateSymptomDuration,
  handleTriggerChange,
  handleUpdateMoodLevel,
  handlePainEntryFormSubmission,
  handleUpdateErrorsOfForm,
  handleUpdatePainLocations,
  handleResetForm,
} = painEntryFromSlice.actions;

export default painEntryFromSlice.reducer;
